import * as React from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Loader2 } from "lucide-react";
import type { BillingResetRequest, InstanceSubscription } from "@/types/api";
import { errorCode } from "@/lib/api-errors";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSubscriptionOps } from "./use-billing-ops";

const ENDED = ["none", "canceled", "failed"];

interface Props {
  name: string;
  subscription: InstanceSubscription | null;
  /** Billing-exempt account: a plain reset does not block it. */
  internal: boolean;
}

/** "resubscribe": reset + send_invoice subscription; "block": plain reset. */
type ResetMode = "resubscribe" | "block";

/**
 * Whether the reset is worth offering: Stripe no longer knows the stored
 * subscription (test-mode leftover), or the subscription is over and the
 * instance still points at Stripe objects.
 */
export function canResetBilling(subscription: InstanceSubscription | null): boolean {
  if (!subscription) return false;
  if (subscription.stripeRefMissing) return true;
  const hasStripeRefs = Boolean(subscription.stripeCustomerId || subscription.stripeSubscriptionId);
  return ENDED.includes(subscription.status ?? "none") && hasStripeRefs;
}

/**
 * Billing reset: forget the instance's Stripe customer/subscription and
 * archive its invoice rows so it can subscribe again — the cleanup after the
 * test -> live switch. The backend refuses while a subscription still runs.
 *
 * A reset leaves the subscription at "none", which the billing gate blocks
 * for every non-internal instance. So for those the dialog defaults to
 * "reset and subscribe again by invoice" (one request, checked before
 * anything changes); a plain reset needs an explicit choice with a warning.
 */
export function BillingResetCard({ name, subscription, internal }: Props) {
  const { t } = useTranslation("billing");
  const ops = useSubscriptionOps(name);
  const [open, setOpen] = React.useState(false);

  if (!canResetBilling(subscription)) return null;
  const refMissing = Boolean(subscription?.stripeRefMissing);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("ops.reset.title")}</CardTitle>
        <CardDescription>{t("ops.reset.cardDescription")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {refMissing ? (
          <div
            role="alert"
            className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <p>{t("ops.reset.refMissingBanner")}</p>
          </div>
        ) : null}
        <Button variant="destructive" onClick={() => setOpen(true)} disabled={ops.reset.isPending}>
          {t("ops.reset.button")}
        </Button>
      </CardContent>

      <ResetDialog
        open={open}
        onOpenChange={setOpen}
        name={name}
        internal={internal}
        pending={ops.reset.isPending}
        onConfirm={(body) =>
          ops.reset.mutate(body, {
            onSuccess: () => setOpen(false),
            onError: (err) => {
              // The reset already happened; keeping the dialog open would
              // invite a second reset instead of a plain subscribe.
              if (errorCode(err) === "RESUBSCRIBE_FAILED") setOpen(false);
            },
          })
        }
      />
    </Card>
  );
}

function ResetDialog({
  open,
  onOpenChange,
  name,
  internal,
  pending,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  name: string;
  internal: boolean;
  pending: boolean;
  onConfirm: (body: BillingResetRequest) => void;
}) {
  const { t } = useTranslation("billing");
  const [typed, setTyped] = React.useState("");
  const [mode, setMode] = React.useState<ResetMode>("resubscribe");
  const inputId = React.useId();
  const modeName = React.useId();
  // One key per dialog opening: a double submit cannot create two subscriptions.
  const idemKey = React.useMemo(() => (open ? crypto.randomUUID() : ""), [open]);

  React.useEffect(() => {
    if (open) {
      setTyped("");
      setMode("resubscribe");
    }
  }, [open]);

  const matches = typed === name;
  const buildBody = (): BillingResetRequest => {
    if (internal) return { confirm: typed };
    return mode === "resubscribe"
      ? { confirm: typed, resubscribe: true, idemKey }
      : { confirm: typed, allowBlock: true };
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !pending && onOpenChange(v)}>
      <DialogContent closeLabel={t("ops.close")}>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (matches && !pending) onConfirm(buildBody());
          }}
        >
          <DialogHeader>
            <DialogTitle>{t("ops.reset.dialogTitle", { name })}</DialogTitle>
            <DialogDescription>{t("ops.reset.dialogDescription")}</DialogDescription>
          </DialogHeader>
          <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
            <li>{t("ops.reset.effects.ids")}</li>
            <li>{t("ops.reset.effects.payments")}</li>
            <li>{t("ops.reset.effects.credits")}</li>
            {internal || mode === "block" ? <li>{t("ops.reset.effects.next")}</li> : null}
          </ul>
          {internal ? null : (
            <fieldset className="space-y-2">
              <legend className="mb-1 text-sm font-medium">{t("ops.reset.mode.legend")}</legend>
              <label className="flex cursor-pointer gap-2 rounded-md border p-3 text-sm">
                <input
                  type="radio"
                  name={modeName}
                  value="resubscribe"
                  checked={mode === "resubscribe"}
                  onChange={() => setMode("resubscribe")}
                  className="mt-0.5"
                />
                <span>
                  <span className="font-medium">{t("ops.reset.mode.resubscribe")}</span>
                  <span className="block text-muted-foreground">
                    {t("ops.reset.mode.resubscribeHint")}
                  </span>
                </span>
              </label>
              <label className="flex cursor-pointer gap-2 rounded-md border p-3 text-sm">
                <input
                  type="radio"
                  name={modeName}
                  value="block"
                  checked={mode === "block"}
                  onChange={() => setMode("block")}
                  className="mt-0.5"
                />
                <span>
                  <span className="font-medium">{t("ops.reset.mode.block")}</span>
                  <span className="block text-muted-foreground">{t("ops.reset.mode.blockHint")}</span>
                </span>
              </label>
              {mode === "block" ? (
                <div
                  role="alert"
                  className="flex gap-2 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-200"
                >
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                  <p>{t("ops.reset.blockWarning")}</p>
                </div>
              ) : null}
            </fieldset>
          )}
          <div className="space-y-1.5">
            <Label htmlFor={inputId}>{t("ops.reset.typeName", { name })}</Label>
            <Input
              id={inputId}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={name}
              autoComplete="off"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
              {t("ops.cancelAction")}
            </Button>
            <Button type="submit" variant="destructive" disabled={!matches || pending}>
              {pending && <Loader2 className="h-4 w-4 animate-spin" />}
              {!internal && mode === "resubscribe"
                ? t("ops.reset.confirmResubscribe")
                : t("ops.reset.confirm")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
